/* Created by Interactive 63 - https://interactive63.com */
/**
 * Base class for editor panels and dialogs. A component owns a root element and cleans up every
 * store subscription and event listener it registered when destroyed. A component whose setup or
 * update throws shows a short notice in place of its content, so one broken panel cannot take
 * the rest of the editor down with it.
 */
import { h } from './dom';
import type { AppState, SelectOptions, Store } from '../store/store';

export abstract class Component<P = void> {
  readonly el: HTMLElement;
  private cleanups: (() => void)[] = [];
  private mounted = false;
  private failed = false;

  /** Creates the component's root element. Props are available as `this.props`. */
  constructor(
    protected store: Store,
    protected props: P,
  ) {
    this.el = this.createRoot();
  }

  /** Called once after mounting. Subscribe to store slices here. */
  protected abstract init(): void;

  /** Creates the root element. Override to use a different tag or add classes. */
  protected createRoot(): HTMLElement {
    return h('div');
  }

  /** Adds the component to a parent and runs `init` once. */
  mount(parent: HTMLElement): this {
    if (this.mounted) return this;
    this.mounted = true;
    parent.append(this.el);
    try {
      this.init();
    } catch (error) {
      this.fail(error);
    }
    return this;
  }

  /** Replaces the panel with a notice and stops it reacting to further changes. */
  private fail(error: unknown): void {
    console.error(error);
    if (this.failed) return;
    this.failed = true;
    for (const cleanup of this.cleanups.splice(0)) cleanup();
    this.el.replaceChildren(
      h(
        'p',
        { class: 'panel-error', role: 'alert' },
        'This panel hit a problem. Reload to try again.',
      ),
    );
  }

  /** Registers a function to run when the component is destroyed. */
  protected addCleanup(fn: () => void): void {
    this.cleanups.push(fn);
  }

  /** Subscribes to a store slice; the subscription is removed on `destroy`. */
  protected select<T>(
    selector: (state: AppState) => T,
    listener: (value: T, prev: T) => void,
    options?: SelectOptions,
  ): void {
    this.cleanups.push(
      this.store.select(
        selector,
        (value, prev) => {
          if (this.failed) return;
          try {
            listener(value, prev);
          } catch (error) {
            this.fail(error);
          }
        },
        options,
      ),
    );
  }

  /** Registers an event listener that is removed on `destroy`. */
  protected listen<K extends keyof WindowEventMap>(
    target: Window,
    type: K,
    handler: (event: WindowEventMap[K]) => void,
    options?: AddEventListenerOptions,
  ): void;
  protected listen<K extends keyof DocumentEventMap>(
    target: Document,
    type: K,
    handler: (event: DocumentEventMap[K]) => void,
    options?: AddEventListenerOptions,
  ): void;
  protected listen<K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    type: K,
    handler: (event: HTMLElementEventMap[K]) => void,
    options?: AddEventListenerOptions,
  ): void;
  protected listen(
    target: EventTarget,
    type: string,
    handler: (event: never) => void,
    options?: AddEventListenerOptions,
  ): void {
    const listener = handler as EventListener;
    target.addEventListener(type, listener, options);
    this.cleanups.push(() => {
      target.removeEventListener(type, listener, options);
    });
  }

  /** Tears down subscriptions and listeners and removes the element. Safe to call twice. */
  destroy(): void {
    for (const cleanup of this.cleanups.splice(0)) cleanup();
    this.el.remove();
  }
}
